// src/components/roles/RoleWizard/WizardStep2.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  FileDownloadOutlined as DownloadIcon,
  DeleteOutline as DeleteIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  SwapHoriz as TransferIcon,
  ContentCopy as CopyIcon,
  Block as BlockIcon,
  CheckBox as CheckBoxIcon,
  Payment as PaymentIcon,
  Undo as ReturnIcon,
  CheckCircle as CheckCircleIcon,
  Lock as ReserveIcon,
  LockOpen as UnreserveIcon,
  Event as EventIcon,
  FormatListNumbered as FormatListNumberedIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { getModuleIcon } from '../../../constants/module-icons';
import { RoleWizardData } from '../../../types/role.types';
import {
  ModuleConfig,
  MenuConfig,
  menuBindsViewRowToMenuVisibility,
  MENU_VIEW_ROW_ACTION_ID,
} from '../../../config/system-structure';
import { UseRoleWizardReturn } from './RoleWizard.hooks';
import { FlagSwitch } from '../../../components/ui';

interface WizardStep2Props {
  formData: RoleWizardData;
  wizard: UseRoleWizardReturn;
  structure: ModuleConfig[];
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  add: <AddIcon fontSize="small" />,
  export: <DownloadIcon fontSize="small" />,
  delete: <DeleteIcon fontSize="small" />,
  view: <ViewIcon fontSize="small" />,
  edit: <EditIcon fontSize="small" />,
  cancel: <CancelIcon fontSize="small" />,
  print: <PrintIcon fontSize="small" />,
  label_print: <ReceiptIcon fontSize="small" />,
  transfer: <TransferIcon fontSize="small" />,
  copy: <CopyIcon fontSize="small" />,
  duplicate: <CopyIcon fontSize="small" />,
  block: <BlockIcon fontSize="small" />,
  pay: <PaymentIcon fontSize="small" />,
  return: <ReturnIcon fontSize="small" />,
  exchange: <TransferIcon fontSize="small" />,
  approve: <CheckCircleIcon fontSize="small" />,
  reject: <CancelIcon fontSize="small" />,
  reserve: <ReserveIcon fontSize="small" />,
  unreserve: <UnreserveIcon fontSize="small" />,
  modify_sale_date: <EventIcon fontSize="small" />,
  toggle_require_counting: <FormatListNumberedIcon fontSize="small" />,
  kpi_in_transit: <LocalShippingIcon fontSize="small" />,
  kpi_warehouse_reserved: <ReserveIcon fontSize="small" />,
};

const WizardStep2: React.FC<WizardStep2Props> = ({ formData, wizard, structure }) => {
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const handleModuleExpand = (moduleId: string) => {
    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const getModulePermissionCount = (module: ModuleConfig): number => {
    const modulePerms = formData.permissions[module.id];
    if (!modulePerms?.enabled) return 0;

    let count = 0;
    Object.values(modulePerms.menus).forEach((menu) => {
      if (menu.visible) count++;
      count += Object.values(menu.mainButtons).filter((val) => val).length;
      count += Object.values(menu.rowActions).filter((val) => val).length;
      if (menu.columnFlags) {
        count += Object.values(menu.columnFlags).filter((val) => val).length;
      }
      if (menu.salesFlowActions) {
        count += Object.values(menu.salesFlowActions).filter((val) => val).length;
      }
    });
    return count;
  };

  const getMenuPermissionCount = (moduleId: string, menu: MenuConfig): number => {
    const menuPerms = formData.permissions[moduleId]?.menus[menu.id];
    if (!menuPerms) return 0;

    let count = menuPerms.visible ? 1 : 0;
    count += Object.values(menuPerms.mainButtons).filter((val) => val).length;
    count += Object.values(menuPerms.rowActions).filter((val) => val).length;
    if (menuPerms.columnFlags) {
      count += Object.values(menuPerms.columnFlags).filter((val) => val).length;
    }
    if (menuPerms.salesFlowActions) {
      count += Object.values(menuPerms.salesFlowActions).filter((val) => val).length;
    }
    return count;
  };

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Configurar Permisos
        </Typography>
        <Chip
          label={`${wizard.totalPermissions} permisos activos`}
          size="small"
          sx={{
            backgroundColor: '#10b981',
            color: 'white',
            fontWeight: 600,
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Selecciona los módulos, menús y acciones permitidas para este rol
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {structure.map((module) => {
          const modulePerms = formData.permissions[module.id];
          const isModuleEnabled = modulePerms?.enabled || false;
          const permissionCount = getModulePermissionCount(module);
          const isExpanded = expandedModules.has(module.id);

          return (
            <Accordion
              key={module.id}
              expanded={isExpanded}
              onChange={() => handleModuleExpand(module.id)}
              sx={{
                border: '1px solid',
                borderColor: isModuleEnabled ? '#10b981' : 'divider',
                borderRadius: 1.5,
                '&:before': { display: 'none' },
                boxShadow: isModuleEnabled ? '0 1px 4px rgba(16, 185, 129, 0.08)' : 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 2,
                  py: 1.5,
                  minHeight: 0,
                  '&.Mui-expanded': { minHeight: 0 },
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 1.5,
                    margin: 0,
                  },
                  '& .MuiAccordionSummary-content.Mui-expanded': {
                    margin: 0,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: isModuleEnabled ? '#10b981' : 'grey.200',
                      color: isModuleEnabled ? 'white' : 'grey.600',
                    }}
                  >
                    {getModuleIcon(module.id)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {module.name.toUpperCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {module.menus.length} menú{module.menus.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  {permissionCount > 0 && (
                    <Chip
                      label={`${permissionCount} permisos`}
                      size="small"
                      sx={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ ml: 2 }} onClick={(e) => e.stopPropagation()}>
                  <FlagSwitch
                    checked={isModuleEnabled}
                    onChange={() => wizard.toggleModule(module.id)}
                    activeColor="success"
                  />
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                {isModuleEnabled ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {module.menus.map((menu) => {
                      const menuPerms = modulePerms.menus[menu.id];
                      const isMenuVisible = menuPerms?.visible || false;
                      const menuPermissionCount = getMenuPermissionCount(module.id, menu);

                      return (
                        <Box
                          key={menu.id}
                          sx={{
                            border: '1px solid',
                            borderColor: isMenuVisible ? '#06b6d4' : 'divider',
                            borderRadius: 1.5,
                            p: 1.5,
                            backgroundColor: isMenuVisible
                              ? 'rgba(6, 182, 212, 0.04)'
                              : 'transparent',
                          }}
                        >
                          {/* Menu Header */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1.5,
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {menu.name}
                              </Typography>
                              {menuPermissionCount > 0 && (
                                <Chip
                                  label={`${menuPermissionCount} permisos`}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#06b6d4',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    height: 20,
                                  }}
                                />
                              )}
                            </Box>
                            <FlagSwitch
                              checked={isMenuVisible}
                              onChange={() => wizard.toggleMenu(module.id, menu.id)}
                              disabled={!isModuleEnabled}
                              activeColor="info"
                              showLabel
                              label={
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                  {isMenuVisible ? 'Visible' : 'Oculto'}
                                </Typography>
                              }
                            />
                          </Box>

                          {isMenuVisible && (
                            <>
                              {/* full / menu-only: sin UI granular. bundle-add: solo Añadir. custom: botones y fila. */}
                              {menu.granularity === 'full' ||
                              menu.granularity === 'menu-only' ? null : menu.granularity ===
                                'bundle-add' ? (
                                <>
                                  <Divider sx={{ my: 1.5 }} />
                                  <Box>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                                    >
                                      Gestión del catálogo
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
                                    >
                                      Incluye añadir, editar, duplicar, exportar y eliminar. Sin
                                      marcar: solo lectura (listar y ver).
                                    </Typography>
                                    {menu.mainButtons?.map((button) => {
                                      const isChecked = menuPerms.mainButtons[button.id] || false;
                                      return (
                                        <FormControlLabel
                                          key={button.id}
                                          control={
                                            <Checkbox
                                              checked={isChecked}
                                              onChange={() =>
                                                wizard.toggleMainButton(
                                                  module.id,
                                                  menu.id,
                                                  button.id,
                                                )
                                              }
                                              sx={{
                                                color: '#06b6d4',
                                                '&.Mui-checked': { color: '#10b981' },
                                              }}
                                            />
                                          }
                                          label={
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                              }}
                                            >
                                              {ACTION_ICONS[button.action] || (
                                                <AddIcon fontSize="small" />
                                              )}
                                              <Typography variant="body2">{button.name}</Typography>
                                            </Box>
                                          }
                                        />
                                      );
                                    })}
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <Divider sx={{ my: 1.5 }} />
                                  {/* Main Buttons */}
                                  {menu.mainButtons && menu.mainButtons.length > 0 && (
                                    <Box>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ fontWeight: 600, color: 'text.primary' }}
                                        >
                                          Botones Principales
                                        </Typography>
                                        <Tooltip title="Seleccionar todos">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              wizard.selectAllMainButtons(module.id, menu.id)
                                            }
                                            sx={{ color: 'primary.main' }}
                                          >
                                            <CheckBoxIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                      <Grid container spacing={1.5}>
                                        {menu.mainButtons.map((button) => {
                                          const isChecked =
                                            menuPerms.mainButtons[button.id] || false;
                                          return (
                                            <Grid item xs={12} sm={6} md={4} key={button.id}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={isChecked}
                                                    onChange={() =>
                                                      wizard.toggleMainButton(
                                                        module.id,
                                                        menu.id,
                                                        button.id,
                                                      )
                                                    }
                                                    sx={{
                                                      color: '#06b6d4',
                                                      '&.Mui-checked': {
                                                        color: '#10b981',
                                                      },
                                                    }}
                                                  />
                                                }
                                                label={
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 1,
                                                    }}
                                                  >
                                                    {ACTION_ICONS[button.action] || (
                                                      <AddIcon fontSize="small" />
                                                    )}
                                                    <Typography variant="body2">
                                                      {button.name}
                                                    </Typography>
                                                  </Box>
                                                }
                                              />
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                    </Box>
                                  )}

                                  {/* Row Actions */}
                                  {menu.rowActions && menu.rowActions.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ fontWeight: 600, color: 'text.primary' }}
                                        >
                                          Acciones de Fila
                                        </Typography>
                                        <Tooltip title="Seleccionar todos">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              wizard.selectAllRowActions(module.id, menu.id)
                                            }
                                            sx={{ color: 'primary.main' }}
                                          >
                                            <CheckBoxIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                      <Grid container spacing={1.5}>
                                        {menu.rowActions.map((action) => {
                                          const isChecked =
                                            menuPerms.rowActions[action.id] || false;
                                          const viewLockedWithMenu =
                                            isMenuVisible &&
                                            menuBindsViewRowToMenuVisibility(menu.id) &&
                                            action.id === MENU_VIEW_ROW_ACTION_ID;
                                          return (
                                            <Grid item xs={12} sm={6} md={4} key={action.id}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={isChecked || viewLockedWithMenu}
                                                    disabled={viewLockedWithMenu}
                                                    onChange={() =>
                                                      wizard.toggleRowAction(
                                                        module.id,
                                                        menu.id,
                                                        action.id,
                                                      )
                                                    }
                                                    sx={{
                                                      color: '#06b6d4',
                                                      '&.Mui-checked': {
                                                        color: '#10b981',
                                                      },
                                                    }}
                                                  />
                                                }
                                                label={
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 1,
                                                    }}
                                                  >
                                                    {ACTION_ICONS[action.action] || (
                                                      <ViewIcon fontSize="small" />
                                                    )}
                                                    <Typography variant="body2">
                                                      {action.name}
                                                    </Typography>
                                                  </Box>
                                                }
                                              />
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                    </Box>
                                  )}

                                  {/* Flujo de venta */}
                                  {menu.salesFlowActions && menu.salesFlowActions.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ fontWeight: 600, color: 'text.primary' }}
                                        >
                                          {menu.flowSectionTitle ?? 'Flujos'}
                                        </Typography>
                                      </Box>
                                      <Grid container spacing={1.5}>
                                        {menu.salesFlowActions.map((flow) => {
                                          const isChecked =
                                            menuPerms.salesFlowActions?.[flow.id] ?? false;
                                          return (
                                            <Grid item xs={12} sm={6} md={4} key={flow.id}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={isChecked}
                                                    onChange={() =>
                                                      wizard.toggleSalesFlowAction(
                                                        module.id,
                                                        menu.id,
                                                        flow.id,
                                                      )
                                                    }
                                                    sx={{
                                                      color: '#06b6d4',
                                                      '&.Mui-checked': {
                                                        color: '#10b981',
                                                      },
                                                    }}
                                                  />
                                                }
                                                label={
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      gap: 0.5,
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                      }}
                                                    >
                                                      {ACTION_ICONS[flow.action] || (
                                                        <EventIcon fontSize="small" />
                                                      )}
                                                      <Typography variant="body2">
                                                        {flow.name}
                                                      </Typography>
                                                    </Box>
                                                    {flow.description && (
                                                      <Typography
                                                        variant="caption"
                                                        sx={{ color: 'text.secondary', ml: 4 }}
                                                      >
                                                        {flow.description}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                }
                                              />
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                    </Box>
                                  )}

                                  {/* Column Flags */}
                                  {menu.columnFlags && menu.columnFlags.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ fontWeight: 600, color: 'text.primary' }}
                                        >
                                          KPIs del dashboard
                                        </Typography>
                                      </Box>
                                      <Grid container spacing={1.5}>
                                        {menu.columnFlags.map((flag) => {
                                          const isChecked =
                                            menuPerms.columnFlags?.[flag.id] || false;
                                          return (
                                            <Grid item xs={12} sm={6} md={4} key={flag.id}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={isChecked}
                                                    onChange={() =>
                                                      wizard.toggleColumnFlag(
                                                        module.id,
                                                        menu.id,
                                                        flag.id,
                                                      )
                                                    }
                                                    sx={{
                                                      color: '#06b6d4',
                                                      '&.Mui-checked': {
                                                        color: '#10b981',
                                                      },
                                                    }}
                                                  />
                                                }
                                                label={
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      gap: 0.5,
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                      }}
                                                    >
                                                      {ACTION_ICONS[flag.action] || (
                                                        <BlockIcon fontSize="small" />
                                                      )}
                                                      <Typography variant="body2">
                                                        {flag.name}
                                                      </Typography>
                                                    </Box>
                                                    {flag.description && (
                                                      <Typography
                                                        variant="caption"
                                                        sx={{ color: 'text.secondary', ml: 4 }}
                                                      >
                                                        {flag.description}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                }
                                              />
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                    </Box>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}
                  >
                    Activa el módulo para configurar permisos
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};

export default WizardStep2;
